import { useDispatch, useSelector } from 'react-redux';
import { goBack, push as navigateTo } from 'connected-react-router';
import { State } from 'reducers';
import { Dialog } from 'ui/shared/containers';
import { getBuildingByUrlSlug, getBuildingDevice, HasDeviceId, HasUrlSlug } from 'selectors/buildingsSelectors';
import { useLocation, useRouteMatch } from 'react-router';
import { useCallback, useEffect, useState } from 'react';
import { Device } from 'shared/models';
import { createDevicesSuccess, updateDeviceSuccess, deleteDeviceSuccess } from 'actions/deviceActions';
import { callApi } from 'apiClient';
import { useTranslation } from 'react-i18next';
import { ConfirmationDialog } from 'ui/shell/confirmationDialog/ConfirmationDialog';
import { expandMacAddress } from 'util/cleanMacAddress';
import { toError } from 'shared/util/toError';
import { EditDeviceForm } from './editDeviceForm/EditDeviceForm';
import { useAsync } from 'hooks';

export type Props = {
    visible: boolean;
};

export const EditDeviceDialog = ({ visible }: Props) => {
    const { t } = useTranslation();
    const routeMatch = useRouteMatch<HasDeviceId & HasUrlSlug>('/buildings/:urlSlug/devices/:deviceId');
    const { isMounted } = useAsync();

    const building = useSelector((state: State) => routeMatch ? getBuildingByUrlSlug(state, routeMatch.params) : null);
    const buildingId = building && building.id;
    const urlSlug = building && building.urlSlug;
    const device = useSelector((state: State) => buildingId ? getBuildingDevice(state, { deviceId: routeMatch!.params.deviceId, buildingId }) : null);
    const deviceId = device && device.id;

    const dispatch = useDispatch();

    const [ busy, setBusy ] = useState(false);
    const [ pin, setPin ] = useState('');
    const [ pinPending, setPinPending ] = useState(false);
    const [ errorMessage, setErrorMessage ] = useState('');
    const [ deleteModalVisible, setDeleteModalVisible ] = useState(false);

    const handleSubmit = useCallback((deviceProperties: Device) => {
        if (!urlSlug) return;
        const { id, buildingId } = deviceProperties;
        setBusy(true);
        void (async () => {
            try {
                const device: Device = await callApi('PUT', `/api/buildings/${buildingId}/devices/${id}`, deviceProperties);
                dispatch(updateDeviceSuccess(device));
                dispatch(navigateTo(`/buildings/${urlSlug}/devices/${id}`));
            }
            catch (e) {
                if (!isMounted()) return;
                const err = toError(e);
                const errorMessage = t([ `DEVICES.ERROR.${err.message}`, 'SERVER_ERROR' ]);
                setErrorMessage(errorMessage);
                setBusy(false);
            }
        })();
    }, [ urlSlug, dispatch, setBusy, setErrorMessage, t ]);

    const handleDeleteConfirmed = useCallback(() => {
        if (!deviceId || !buildingId) return;
        setBusy(true);
        setErrorMessage('');
        void (async () => {
            try {
                setDeleteModalVisible(false);
                await callApi('DELETE', `/api/buildings/${buildingId}/devices/${deviceId}`);
                dispatch(deleteDeviceSuccess({ deviceId, buildingId }));
                dispatch(navigateTo(`/buildings/${urlSlug}/devices`));
            }
            catch (e) {
                if (!isMounted()) return;
                const err = toError(e);
                setErrorMessage(t([ err.message, 'SERVER_ERROR' ]));
                console.error(err);
                setBusy(false);
            }
        })();
    }, [ buildingId, deviceId, urlSlug, setBusy, setErrorMessage, setDeleteModalVisible, dispatch, t, isMounted ]);

    const handleCreatePin = useCallback(() => {
        setPin('');
        setPinPending(true);
        void (async () => {
            try {
                const pin: string = await callApi('POST', `/api/buildings/${buildingId}/devices/${deviceId}/pin`);
                if (!isMounted()) return;
                setPin(pin);
                setErrorMessage('');
            }
            catch (e) {
                if (!isMounted()) return;
                const err = toError(e);
                const errorMessage = t([ err.message, 'SERVER_ERROR' ]);
                setErrorMessage(errorMessage);
            }
            finally {
                if (isMounted()) {
                    setPinPending(false);
                }
            }
        })();
    }, [ buildingId, deviceId, setPin, setPinPending, setErrorMessage, t, isMounted ]);

    // Reset the dialog if the building/device changes
    useEffect(() => {
        setPin('');
        setPinPending(false);
        setErrorMessage('');
        setBusy(false);
    }, [ setPin, setPinPending, setErrorMessage, setBusy, buildingId, deviceId ]);

    // Don't show any UI until the data has loaded
    if (!building || !device) {
        return null;
    }

    const deletePromptMessage =
        device.displayName
            ? t('DEVICES.DELETE_CONFIRMATION_WITH_NAME', [ device.displayName ])
            : device.macAddress
                ? t('DEVICES.DELETE_CONFIRMATION_WITH_MACADDRESS', [ expandMacAddress(device.macAddress) ])
                : t('DEVICES.DELETE_CONFIRMATION_WITH_ID', [ device.id ]);

    return (
        <Dialog
            id="edit-device-dialog"
            visible={ visible }
            titleKey={ `DEVICES.EDIT_DIALOG.EDIT_TITLE` }
        >
            <EditDeviceForm
                busy={ busy }
                device={ device }
                buildingId={ building.id }
                pin={ pin }
                pinPending={ pinPending }
                readOnly={ !!building?.properties.remoteUpdateDisabled }
                errorMessage={ errorMessage }
                onSubmit={ handleSubmit }
                onClickCancel={ () => dispatch(goBack()) }
                onClickDelete={ () => setDeleteModalVisible(true) }
                onCreatePin={ handleCreatePin }
            />
            <ConfirmationDialog
                id="delete-device-confirmation-dialog"
                message={ deletePromptMessage }
                visible={ deleteModalVisible }
                onClickNo={ () => setDeleteModalVisible(false) }
                onClickYes={ handleDeleteConfirmed }
            />
        </Dialog>
    );
};
